import { fetchCampaignById, fetchCampaigns } from '../../../api/campaigns';

export function formatProductPriceEur(price) {
  const n = Number(price);
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 2,
  }).format(n);
}

function normalizeEntityId(value) {
  return String(value?._id ?? value?.id ?? value ?? '').trim();
}

export async function fetchCampaignsForPicker() {
  const res = await fetchCampaigns();
  const campaigns = res?.data?.campaigns || res?.data || [];
  return (Array.isArray(campaigns) ? campaigns : [])
    .filter((c) => c.status !== 'draft')
    .map((c) => ({
    id: normalizeEntityId(c),
    name: c.name || 'Campagne',
    stancerConfigured: Boolean(c.stancerKeysConfigured),
    products: (c.products || []).map((p) => ({
      id: normalizeEntityId(p),
      name: p.name || 'Produit',
      description: p.description || '',
      price: typeof p.price === 'number' ? p.price : 0,
    })),
  }));
}

export async function fetchCampaignWithProducts(campaignId) {
  if (!campaignId) return null;
  const res = await fetchCampaignById(campaignId);
  const c = res?.data?.campaign;
  if (!c) return null;
  return {
    id: normalizeEntityId(c),
    name: c.name || 'Campagne',
    stancerConfigured: Boolean(c.stancerKeysConfigured),
    products: (c.products || []).map((p) => ({
      id: normalizeEntityId(p),
      name: p.name || 'Produit',
      description: p.description || '',
      price: typeof p.price === 'number' ? p.price : 0,
    })),
  };
}

export function resolveProductDisplay(props = {}) {
  const inheritTitle = props.inheritTitle !== false;
  const inheritDescription = props.inheritDescription !== false;
  return {
    title: inheritTitle
      ? props.productName || props.title || 'Produit'
      : props.title || props.productName || 'Produit',
    description: inheritDescription
      ? props.productDescription ?? props.description ?? ''
      : props.description ?? props.productDescription ?? '',
  };
}

export function productPickerPatch(campaign, product, existingProps = {}) {
  if (!campaign || !product) {
    return {
      campaignId: '',
      productId: '',
      price: null,
      stancerConfigured: false,
      productName: '',
      productDescription: '',
    };
  }
  const inheritTitle = existingProps.inheritTitle !== false;
  const inheritDescription = existingProps.inheritDescription !== false;
  return {
    campaignId: campaign.id,
    productId: product.id,
    price: product.price,
    stancerConfigured: campaign.stancerConfigured,
    productName: product.name || '',
    productDescription: product.description || '',
    inheritTitle,
    inheritDescription,
    title: inheritTitle ? existingProps.title || '' : existingProps.title || '',
    description: inheritDescription ? existingProps.description || '' : existingProps.description || '',
  };
}

export async function refreshCampaignProductProps(comp) {
  if (!comp?.props?.campaignId || !comp?.props?.productId) return comp.props;
  const campaign = await fetchCampaignWithProducts(comp.props.campaignId);
  if (!campaign) return comp.props;
  const product = campaign.products.find(
    (p) => normalizeEntityId(p.id) === normalizeEntityId(comp.props.productId),
  );
  if (!product) return comp.props;
  return {
    ...comp.props,
    price: product.price,
    stancerConfigured: campaign.stancerConfigured,
    productName: product.name || '',
    productDescription: product.description || '',
  };
}

export async function refreshCampaignSnapshotsInSite(site) {
  if (!site?.sections?.length) return site;

  const sections = await Promise.all(
    site.sections.map(async (sec) => {
      const components = await Promise.all(
        (sec.components || []).map(async (comp) => {
          if (comp.type !== 'product') return comp;
          if (!comp.props?.campaignId || !comp.props?.productId) return comp;
          const props = await refreshCampaignProductProps(comp);
          return { ...comp, props };
        }),
      );
      return { ...sec, components };
    }),
  );

  return { ...site, sections };
}
